import React, { useEffect, useMemo } from 'react';
import { Header, Title } from '../ShelfPage/styles';
import DateRangeDropdown from '../components/DateRangeDropdown';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { useAppSelector, useHistory, useLocation } from 'src/utils/Hooks';
import { useUpdateQueryParams } from 'src/utils/Hooks/useUpdateQueryParams';
import { useGenericPopup } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';
import SummaryCampaignOverview from '../components/SummaryCampaignOverview';
import SummaryCampaignConfiguration from '../components/SummaryCampaignConfiguration';
import { DateRangeValue, isTimestampWithinRange } from '../shared';

export interface SummaryCampaignDetailsProps {
    campaign: any;
    onBack: () => void;
    campaignId?: string | number | null;
    onSaved?: () => void;
}

const SummaryCampaignDetails: React.FC<SummaryCampaignDetailsProps> = ({ campaign, onBack, campaignId: campaignIdProp, onSaved }) => {
    const name = campaign?.name || campaign?.campaignName || campaign?.title || '—';
    const template = campaign?.templateType || campaign?.type || campaign?.category || '—';
    const statusRaw = (campaign?.status || '').toString().toLowerCase();
    const divisionId = useAppSelector((state) => _get(state, ['user', 'session', 'divisionId'], null));

    const [batches, setBatches] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [range, setRange] = React.useState<DateRangeValue>('all');
    const campaignId = useMemo(() => {
        if (campaignIdProp) return campaignIdProp;
        return (
            campaign?.id ||
            campaign?.campaignId ||
            campaign?.crawlCampaignId ||
            campaign?.crawl_campaign_id ||
            campaign?.campaign_id ||
            campaign?.uuid ||
            null
        );
    }, [campaign, campaignIdProp]);

    const normalizeBatchesResponse = (data: any) => {
        if (Array.isArray(data?.batches)) return data.batches;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data)) return data;
        return [] as any[];
    };

    const getStartTimestamp = (batch: any) => {
        const ts = batch?.startedAt || batch?.startTime || batch?.createdAt || null;
        return ts ? new Date(ts).getTime() : -Infinity;
    };

    useEffect(() => {

        let active = true;
        (async () => {
            try {
                setIsLoading(true);
                const path = buildLlmCrawlerPath(`crawl-campaigns/${campaignId}/batches?divisionId=${divisionId}&env=dev`);

                const { data } = await axios.get(path);
                const list = normalizeBatchesResponse(data);
                const sorted = [...list].sort((a, b) => getStartTimestamp(b) - getStartTimestamp(a));
                if (active) setBatches(sorted);
            } catch (e) {
                if (active) setBatches([]);

                console.error('Failed to fetch batches', e);
            } finally {
                if (active) setIsLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [campaignId]);

    const [activeTab, setActiveTab] = React.useState<'overview' | 'configuration'>('overview');
    const [editableName, setEditableName] = React.useState<string>(name);
    const history = useHistory();
    const location = useLocation();
    const updateQuery = useUpdateQueryParams(history);

    // Initialize activeTab from URL param and keep it in sync on navigation
    useEffect(() => {
        try {
            const search = new URLSearchParams(location.search);
            const v = (search.get('shelfCampaignView') || '').toLowerCase();
            if (v === 'overview' || v === 'configuration') {
                setActiveTab(v as any);
            }
        } catch { }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveStatus, setSaveStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
    const { showPopup, closePopup } = useGenericPopup();
    const [localStatus, setLocalStatus] = React.useState<string>(statusRaw);

    const handleSave = async () => {
        if (!campaignId) return;
        const requests: Array<Promise<any>> = [];
        const baseStatus = (statusRaw === 'disabled' || statusRaw === 'inactive') ? 'disabled' : 'enabled';
        const currentStatus = (localStatus === 'disabled' || localStatus === 'inactive') ? 'disabled' : 'enabled';
        const nameChanged = editableName !== name;
        const statusChanged = currentStatus !== baseStatus;
        if (!nameChanged && !statusChanged) return;
        try {
            setIsSaving(true);
            setSaveStatus('idle');
            if (nameChanged) {
                const path = buildLlmCrawlerPath(`crawl-campaigns/${campaignId}/update?env=dev`);
                const payload = { divisionId, name: editableName };
                requests.push(axios.put(path, payload));
            }
            if (statusChanged) {
                const action = currentStatus === 'enabled' ? 'enable' : 'disable';
                const path = buildLlmCrawlerPath(`crawl-campaigns/${campaignId}/${action}?env=dev`);
                requests.push(axios.post(path, {}));
            }
            await Promise.all(requests);
            setSaveStatus('success');
            showPopup({ open: true, width: 300, height: 220, titleMessage: 'Saved', message: 'Agent updated successfully.' });
            setTimeout(async () => {
                closePopup();
                try { await onSaved?.(); } catch { }
                onBack();
            }, 2000);
        } catch (e) {
            setSaveStatus('error');
            showPopup({ open: true, width: 300, height: 220, titleMessage: 'Save failed', message: 'Unable to update campaign. Please try again.' });
            setTimeout(async () => {
                closePopup();
                try { await onSaved?.(); } catch { }
                onBack();
            }, 2500);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredBatches = React.useMemo(() => {
        if (!batches || !batches.length) return batches;
        const now = Date.now();
        return batches.filter((b) => isTimestampWithinRange(getStartTimestamp(b), range, now));
    }, [batches, range]);

    return (
        <>
            <Header>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Title>{name}</Title>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {activeTab === 'overview' ? (
                        <DateRangeDropdown value={range} onChange={setRange} />
                    ) : null}
                    {/* {activeTab === 'overview' ? <ShelfCreditBalanceCard /> : null} */}
                </div>
            </Header>

            <GenericNavigationTabs
                tab={activeTab}
                tabs={[
                    {
                        label: 'Overview',
                        onClick: () => {
                            setActiveTab('overview');
                            updateQuery({ additionalParams: { shelfCampaignView: 'overview' } });
                        },
                        value: 'overview'
                    },
                    {
                        label: 'Configuration',
                        onClick: () => {
                            setActiveTab('configuration');
                            updateQuery({ additionalParams: { shelfCampaignView: 'configuration' } });
                        },
                        value: 'configuration'
                    }
                ]}
            />

            {activeTab === 'overview' && (
                <SummaryCampaignOverview
                    batches={filteredBatches}
                    isLoading={isLoading}
                    onBatchClick={(bid: string) => {
                        try {
                            const base = window.location.origin + window.location.pathname;
                            const search = new URLSearchParams(window.location.search);
                            search.set('batchId', String(bid));
                            window.history.pushState({}, '', `${base}?${search.toString()}`);
                        } catch { }
                        window.dispatchEvent(new CustomEvent('shelf:navigate-data', { detail: { batchId: String(bid) } }));
                    }}
                    range={range}
                    onChangeRange={setRange}
                />
            )}

            {activeTab === 'configuration' && (
                <SummaryCampaignConfiguration
                    template={template}
                    editableName={editableName}
                    setEditableName={setEditableName}
                    createdBy={campaign?.createdBy || null}
                    createdTime={campaign?.createdTime}
                    endTime={campaign?.endTime || null}
                    isSaving={isSaving}
                    saveStatus={saveStatus}
                    onSave={handleSave}
                    campaignId={campaignId}
                    canSave={
                        editableName !== name ||
                        (((localStatus === 'disabled' || localStatus === 'inactive') ? 'disabled' : 'enabled') !==
                            ((statusRaw === 'disabled' || statusRaw === 'inactive') ? 'disabled' : 'enabled'))
                    }
                    status={(localStatus === 'disabled' || localStatus === 'inactive') ? 'disabled' : 'enabled'}
                    isUpdatingStatus={isSaving}
                    onChangeStatus={(next) => {
                        setLocalStatus(next);
                    }}
                />
            )}
        </>
    );
};

export default SummaryCampaignDetails;
